import React from 'react';
import { useFlag } from '@unleash/proxy-client-react';
import Icon from '../shared/Icon';
import { useLibraryPageEnabled } from '../../../utils/useLibraryPageEnabled';
import { useCapabilities, DATA_SOURCES_TABS, SETTINGS_SECTIONS } from '../../../utils/capabilities';
import { useNavyShell, useFlagsDecided, NAVY_PAGES } from '../../../utils/navyShell';
import './Sidebar.css';

// ResAI colour logo — inline so it inherits no link/hover and never 404s.
const BrandMark = () => (
  <div className="sidebar-brand" role="img" aria-label="ResAI">
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M31.1559 6.26534C31.1559 5.85878 30.8261 5.52806 30.4187 5.52806C30.4187 5.52806 30.4187 5.52893 30.4178 5.52893V5.52806C30.4178 5.52806 10.5917 5.52979 10.5908 5.53056C10.2678 5.54237 9.98429 5.71027 9.81648 5.96333C9.81398 5.96419 2.11507 20.1826 2.11507 20.1826C1.88813 20.6701 1.75997 21.2117 1.75574 21.7836V21.8207H22.7561C22.7561 21.8207 22.7586 21.8173 22.7595 21.8156L16.4028 10.1412H29.1332C29.1332 10.1412 31.0531 6.63562 31.0531 6.63475C31.1172 6.52589 31.156 6.40109 31.156 6.26525" fill="#D9E8E6" />
      <path d="M29.1333 10.1416H16.4028L22.7622 21.8211L29.1333 10.1416Z" fill="#1FBCBC" />
      <path d="M46.2307 26.434C46.2307 26.4315 46.2299 26.3968 46.2299 26.3968C46.2265 25.825 46.0975 25.2834 45.8714 24.7958C45.8714 24.7958 38.1726 10.5775 38.17 10.5766C38.0013 10.3244 37.7187 10.1557 37.3948 10.1438C37.3948 10.143 33.4817 10.143 29.1324 10.1421L22.7622 21.8207L25.2733 26.4339H46.2306L46.2307 26.434Z" fill="#00DEFF" />
      <path d="M22.7565 21.8207H1.75536C1.75709 22.4533 1.91146 23.0506 2.18218 23.5769C2.18467 23.5836 9.28214 36.7333 9.28214 36.7342C9.67949 37.5272 10.4968 38.0737 11.4449 38.0737H18.8789L25.2736 26.4339L22.7565 21.8207Z" fill="#1FBCBC" />
      <path d="M31.6443 38.0839H18.8733L25.2732 26.434L31.6443 38.0839Z" fill="#233146" />
      <path d="M25.2733 26.434L31.6445 38.0839H18.8735L17.0253 41.4495C16.9527 41.571 16.9115 41.7127 16.9115 41.8645C16.9115 42.3074 17.2615 42.6667 17.6993 42.6836C17.7078 42.6845 36.5343 42.687 36.5419 42.687C37.4892 42.687 38.3075 42.1404 38.7038 41.3475C38.7047 41.3466 45.8012 28.1969 45.8038 28.1902C46.0754 27.6638 46.229 27.0666 46.2306 26.434H25.2733Z" fill="#135EFF" />
    </svg>
  </div>
);

/**
 * View Component for Sidebar — the navy nav rail (PRD-1414).
 *
 * Which items appear is unchanged: RBAC `features` + Unleash flags gate each
 * one exactly as before, so a user never sees a button for a page they can't
 * open. This rework is visual only — navy rail, active bar, hover/focus
 * tooltips, Phosphor glyphs, and the locked-item treatment.
 *
 * Library renders locked to demonstrate the state until `library_page` is also
 * on (PRD-1605), when it opens the Library page. The final nav order is an open
 * design question (see the handoff) — Design owns it.
 */
const Sidebar = ({ onNavigate, currentPage = 'ask-resai', onLogout, features = {}, activeOrgName, onClearOrgSwitch }) => {
  // Unleash kill-switches for nav items. RBAC features still apply on top —
  // both the flag AND the RBAC bit must be truthy for the item to show.
  const insightsWidgetsEnabled = useFlag('insights_widgets');
  const pushToBiEnabled = useFlag('push_to_bi');
  // PRD-651: when the avatar account menu owns Sign out, drop the sidebar logout.
  // Flag off (default) keeps the sidebar control so rollback is clean.
  const accountMenuFull = useFlag('account-menu');
  // PRD-09: Activity Log (personal history) gated behind its own flag; off = hidden.
  const activityLogEnabled = useFlag('activity-log');
  // PRD-1414: Library sits behind a flag and ships locked for now. Off (default) = hidden.
  const libraryEnabled = useFlag('library');
  // PRD-1605: unlocked by the same check that opens the route in App.js.
  const libraryPageEnabled = useLibraryPageEnabled();
  // PRD-1699: with `role-capabilities` on, these items also need the role's capability.
  const caps = useCapabilities(features);
  const showInsights = caps.enabled ? caps.has('insights') : features.can_access_insights_page !== false;
  const showDataSources = caps.enabled ? caps.any(DATA_SOURCES_TABS) : features.can_access_datasets_page;
  const showSettings = caps.enabled ? caps.any(SETTINGS_SECTIONS) : features.can_access_settings;
  const showLibrary = !caps.enabled || caps.has('library');
  const showActivities = !caps.enabled || caps.has('activities');
  // Navy Phase 0 (handoff "Layout"): only the four built pages, in this order.
  // Nothing is locked, so nothing carries a padlock.
  const navyShell = useNavyShell();
  // Nothing until the flags arrive, so neither rail flashes before the other.
  const flagsDecided = useFlagsDecided();
  const navyItems = {
    'data-sources': { shown: showDataSources, label: 'Data Sources', icon: 'database' },
    library: { shown: showLibrary, label: 'Library', icon: 'folders' },
    'activity-log': { shown: showActivities, label: 'Activities', icon: 'clock-counter-clockwise' },
    settings: { shown: showSettings, label: 'Settings', icon: 'gear-six' },
  };
  const menuItems = !flagsDecided ? [] : navyShell ? NAVY_PAGES
    .filter((id) => navyItems[id].shown)
    .map((id) => ({ id, label: navyItems[id].label, icon: navyItems[id].icon })) : [
    ...(features.can_access_res_ai_page !== false ? [{ id: 'ask-resai', label: 'ResAI', icon: 'chat' }] : []),
    ...(insightsWidgetsEnabled && showInsights ? [{ id: 'insights', label: 'Insights', icon: 'chart-line' }] : []),
    ...(showDataSources ? [{ id: 'data-sources', label: 'Data Sources', icon: 'database' }] : []),
    // PRD-1414: shown when the flag is on; locked until PRD-1605's page flag unlocks it.
    ...(libraryEnabled && showLibrary ? [{ id: 'library', label: 'Library', icon: 'folders', locked: !libraryPageEnabled }] : []),
    ...(features.can_access_automation ? [{ id: 'automations', label: 'Automations', icon: 'lightning' }] : []),
    // PRD-09: Activity Log = the user's OWN history (the spec's "Activities"), gated behind the activity-log flag.
    ...(activityLogEnabled && showActivities ? [{ id: 'activity-log', label: 'Activities', icon: 'clock-counter-clockwise' }] : []),
    // Audit Logs (org-wide, all users) stays for those with the capability, distinct icon.
    ...(features.can_access_audit_logs !== false ? [{ id: 'audit-logs', label: 'Audit Logs', icon: 'list-checks' }] : []),
    ...(pushToBiEnabled && features.can_access_push_to_bi !== false ? [{ id: 'push-to-bi', label: 'Push to BI', icon: 'cloud-arrow-up' }] : []),
    ...(showSettings ? [{ id: 'settings', label: 'Settings', icon: 'gear-six' }] : []),
  ].filter(Boolean);

  const handleNavClick = (item) => {
    if (item.locked) return;
    if (onNavigate) {
      onNavigate(item.id);
    }
  };

  return (
    <nav className="sidebar" aria-label="Primary">
      <div className="sidebar-content">
        <BrandMark />
        <div className="sidebar-nav">
          {menuItems.map((item) => {
            const isActive = !item.locked && currentPage === item.id;
            return (
              <button
                key={item.id}
                type="button"
                className={`nav-item${isActive ? ' active' : ''}${item.locked ? ' nav-item--locked' : ''}`}
                onClick={() => handleNavClick(item)}
                aria-current={isActive ? 'page' : undefined}
                aria-disabled={item.locked ? 'true' : undefined}
              >
                <Icon name={item.icon} size={24} className="nav-glyph" />
                {item.locked && <Icon name="lock" size={16} className="nav-lock" />}
                <span className="nav-label">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      {activeOrgName && (
        <div className="sidebar-org-banner">
          <span className="sidebar-org-banner-text">Viewing as: {activeOrgName}</span>
          <button
            type="button"
            className="sidebar-org-banner-close"
            onClick={onClearOrgSwitch}
            title="Return to ResolveHD"
            aria-label="Clear org switch"
          >
            &times;
          </button>
        </div>
      )}
      {onLogout && !accountMenuFull && (
        <button type="button" className="nav-item sidebar-logout" onClick={onLogout} aria-label="Logout">
          <Icon name="sign-out" size={24} className="nav-glyph" />
          <span className="nav-label">Logout</span>
        </button>
      )}
    </nav>
  );
};

export default Sidebar;
